import type { FormConfig } from './types.ts';

const FORM_SCREEN_IDENTIFIER = 'quick-brick-form-screen';

type FormScreenData = {
  form_feed?: { source?: string } | null;
  source?: string;
};

type FormScreenLike = {
  type?: string;
  data?: FormScreenData | null;
  general?: Record<string, unknown> | null;
};

/**
 * Parses Development local form JSON when that override is enabled.
 *
 * @param screen - Screen configuration object.
 * @returns Parsed form configuration, or null when local JSON is off or invalid.
 */
export function getLocalFormConfig(screen?: FormScreenLike): FormConfig | null {
  const general = screen?.general;
  if (screen?.type !== FORM_SCREEN_IDENTIFIER || !general?.use_local_json) {
    return null;
  }

  const raw = general.local_json;
  if (typeof raw !== 'string' || raw.trim().length === 0) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const properties = (parsed as FormConfig).properties;
    if (!Array.isArray(properties)) return null;

    return { properties: [...properties] };
  } catch {
    return null;
  }
}

/**
 * Resolves the feed source URL for a form screen.
 *
 * @param screen - Screen configuration object.
 * @returns Feed source URL when available.
 */
export function getFormScreenFeedSource(
  screen?: FormScreenLike
): string | undefined {
  if (screen?.type !== FORM_SCREEN_IDENTIFIER) return undefined;

  const formFeedSource = screen?.data?.form_feed?.source;
  if (typeof formFeedSource === 'string' && formFeedSource.length > 0) {
    return formFeedSource;
  }

  return undefined;
}

/**
 * Returns whether the given screen is a Quick Brick form screen.
 *
 * @param screen - Screen configuration object.
 * @returns `true` when the screen type is `quick-brick-form-screen`.
 */
export function isFormScreen(screen?: { type?: string }): boolean {
  return screen?.type === FORM_SCREEN_IDENTIFIER;
}

/**
 * Resolves the primary feed source configured on any screen type.
 *
 * @param screen - Screen configuration object.
 * @returns Feed source URL when available.
 */
export function getScreenFeedDataSource(
  screen?: FormScreenLike
): string | undefined {
  const formScreenFeedSource = getFormScreenFeedSource(screen);
  if (formScreenFeedSource) return formScreenFeedSource;

  const source = screen?.data?.source;
  if (typeof source === 'string' && source.length > 0) return source;

  return undefined;
}
